import React, { useState, useEffect, useMemo } from 'react';
import { Select, Tooltip, Button, Spin, Alert } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import RocketImg from 'assets/marketing/pro-listing-rocket.png';
import WebinarBooking from 'components/WebinarBooking';
import { CALENDLY_LINK } from 'constants/common';
import { EWebinarScheduleType } from 'types/common';
import AddProviderModal from 'components/AddProviderModal';
import { isProviderBudgetExhausted } from 'utils/marketing';
import s from './s.module.less';
import PerformanceChart from '../PerformanceChart';
import ByProviderTable from '../ByProviderTable';
import usePracticeOverview from 'hooks/marketing/usePracticeOverview';
import usePracticeMetrics from 'hooks/marketing/usePracticeMetrics';
import usePracticeMetricsTrend from 'hooks/marketing/usePracticeMetricsTrend';
import usePracticeByProvider from 'hooks/marketing/usePracticeByProvider';

import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import type { PracticeType } from '../../types';
import { BudgetStatus } from 'types/marketing';
import type { IPracticeOverview } from 'types/marketing';

const { Option } = Select;

type PerfBannerVariant = 'amber' | 'blue' | 'upsell';

interface PerfBannerConfig {
    variant: PerfBannerVariant;
    title: string;
    text: string;
    ctaLabel: string;
    ctaAction: 'addProvider' | 'setupProListing';
}

function getPerfBannerConfig(overview: IPracticeOverview): PerfBannerConfig | null {
    const { totalProviders, onProListing } = overview;
    const practiceType: PracticeType = totalProviders < 5 ? 'small' : 'large';

    if (practiceType === 'small') {
        if (onProListing === 0) {
            return {
                variant: 'amber',
                title: `You have ${totalProviders} of ${totalProviders} providers on Standard Listing`,
                text: 'Add more providers to grow your reach — Pro-Listing and premium group features unlock at 5 providers.',
                ctaLabel: '+ Add more providers',
                ctaAction: 'addProvider',
            };
        }
        if (onProListing < totalProviders) {
            const need = 5 - totalProviders;
            return {
                variant: 'blue',
                title: `${onProListing} of ${totalProviders} providers on Pro-Listing — add ${need} more to unlock premium features`,
                text: `Pro-Listing bidding is active for ${onProListing} providers. Group Practice Page, Closed-Loop Booking, and Provider Listing Carousel unlock at 5+ providers on Pro-Listing.`,
                ctaLabel: '+ Add more providers',
                ctaAction: 'addProvider',
            };
        }
        const need = Math.max(5 - onProListing, 1);
        return {
            variant: 'blue',
            title: `All your ${onProListing} providers are on Pro-Listing — add ${need} more to unlock premium features`,
            text: `Pro-Listing bidding is active for all ${onProListing} providers. Group Practice Page, Closed-Loop Booking, and Provider Listing Carousel unlock at 5+ providers on Pro-Listing.`,
            ctaLabel: '+ Add more providers',
            ctaAction: 'addProvider',
        };
    }

    if (onProListing === 0) {
        return {
            variant: 'upsell',
            title: 'All of your providers are currently on Standard Listing — ready to unlock Pro-Listing',
            text: 'Enable Pro-Listing on at least 5 providers to get auction-based top placement, a Group Practice Page with Closed-Loop Booking, and Provider Listing Page Carousel.',
            ctaLabel: 'Enable Pro-Listing',
            ctaAction: 'setupProListing',
        };
    }
    if (onProListing < 5) {
        const need = 5 - onProListing;
        return {
            variant: 'blue',
            title: `${onProListing} of ${totalProviders} providers currently on Pro-Listing — add ${need} more to enable Premium Placement`,
            text: `Pro-Listing bidding is active for ${onProListing} providers. Group Practice Page, Closed-Loop Booking, and Provider Listing Carousel unlock at 5+ providers on Pro-Listing.`,
            ctaLabel: 'Enroll more providers on Pro-Listing',
            ctaAction: 'setupProListing',
        };
    }

    return null;
}

type Props = {
    onSetupClick?: () => void;
};

const GroupPerformance = ({ onSetupClick }: Props) => {
    const [overview, overviewLoading] = usePracticeOverview();
    const hasProListing = (overview?.onProListing ?? 0) > 0;
    const proListingCount = overview?.onProListing ?? 0;
    const stdListingCount = overview?.totalProviders ?? 0;

    const [selectedProvider, setSelectedProvider] = useState('all');
    const [selectedPeriod, setSelectedPeriod] = useState('1');
    const [selectedSpecialty, setSelectedSpecialty] = useState('All specialties');
    const [showWebinarBooking, setShowWebinarBooking] = useState(false);
    const [addProviderOpen, setAddProviderOpen] = useState(false);
    const [trendTab, setTrendTab] = useState<'overview' | 'byProvider'>('overview');
    const [trendProvider, setTrendProvider] = useState('all');
    const [trendSpecialty, setTrendSpecialty] = useState('All specialties');
    const [trendPeriod, setTrendPeriod] = useState('1');

    const getDateRange = (weeks: number) => {
        const endDate = dayjs().format('YYYY-MM-DD');
        const startDate =
            weeks === 0
                ? '2020-01-01'
                : dayjs()
                      .subtract(weeks * 7, 'day')
                      .format('YYYY-MM-DD');
        return { startDate, endDate };
    };

    const [metrics, metricsLoading, fetchMetrics] = usePracticeMetrics({ week: 1 });
    const [trendDisplay, trendLoading, fetchTrend] = usePracticeMetricsTrend({ week: 1 }, metrics.joinedAt);
    const [byProviderRows, , fetchByProvider] = usePracticeByProvider({ ...getDateRange(1) });
    const [allProviders, setAllProviders] = useState(byProviderRows);
    useEffect(() => {
        if (byProviderRows.length > allProviders.length) {
            setAllProviders(byProviderRows);
        }
    }, [byProviderRows]);

    const periodOptions = useMemo(() => {
        const options = [
            { value: '1', label: 'Last 1 week', weeks: 1 },
            { value: '2', label: 'Last 2 weeks', weeks: 2 },
            { value: '4', label: 'Last 4 weeks', weeks: 4 },
            { value: '8', label: 'Last 8 weeks', weeks: 8 },
            { value: '12', label: 'Last 12 weeks', weeks: 12 },
            { value: '0', label: 'Since you started', weeks: 0 },
        ];

        if (!overview?.createdAt) return options.map((o) => ({ ...o, disabled: false }));

        const createdAt = dayjs(overview.createdAt).startOf('day');
        const today = dayjs().startOf('day');
        const daysSinceCreated = today.diff(createdAt, 'day');

        return options.map((o) => ({
            ...o,
            disabled: o.weeks > 1 && o.weeks * 7 > daysSinceCreated,
        }));
    }, [overview?.createdAt]);

    const providerOptions = useMemo(() => {
        return [
            { value: 'all', label: 'All providers' },
            ...allProviders.map((row) => ({ value: String(row.providerId), label: row.name })),
        ];
    }, [allProviders]);

    const specialtyOptions = useMemo(() => {
        const specialties = new Set<string>();
        allProviders.forEach((row) => {
            row.services?.forEach((svc) => {
                if (svc.specialtyName) specialties.add(svc.specialtyName);
            });
        });
        return ['All specialties', ...Array.from(specialties).sort()];
    }, [allProviders]);

    useEffect(() => {
        fetchMetrics({
            week: parseInt(selectedPeriod, 10),
            providerId: selectedProvider === 'all' ? undefined : Number(selectedProvider),
            specialty: selectedSpecialty === 'All specialties' ? undefined : selectedSpecialty,
        });
    }, [selectedPeriod, selectedProvider, selectedSpecialty]);

    useEffect(() => {
        const weeks = parseInt(trendPeriod, 10);
        const providerId = trendProvider === 'all' ? undefined : Number(trendProvider);
        const specialty = trendSpecialty === 'All specialties' ? undefined : trendSpecialty;

        fetchTrend({ week: weeks, providerId, specialty });
        fetchByProvider({ ...getDateRange(weeks), providerId, specialty });
    }, [trendPeriod, trendProvider, trendSpecialty]);

    const formatChange = (value: number) => {
        if (value === 0) return '— 0%';
        const sign = value > 0 ? '↑' : '↓';
        return `${sign} ${Math.abs(value)}%`;
    };

    const tooltips = {
        bookings: hasProListing
            ? 'Total number of new patient appointments booked through both Standard Listing and Pro-Listings.'
            : 'Total number of new patient appointments booked through Standard Listing.',
        impressions: 'How many times your providers were shown to patients in Klarity search results.',
        impressionShare:
            'The percentage of total impressions your providers received compared to all impressions received by similar providers.',
        totalSpend: "The total amount you've spent on Pro-Listings bookings so far.",
    };

    const bannerConfig = overview ? getPerfBannerConfig(overview) : null;

    // Budget-exhausted reminders take priority over the growth/upsell banners.
    const poolExhausted = overview?.poolStatus === BudgetStatus.BUDGET_EXHAUSTED;
    const exhaustedProviderCount = allProviders.filter(isProviderBudgetExhausted).length;
    const showBudgetBanner = poolExhausted || exhaustedProviderCount > 0;

    const budgetBannerRender = () => {
        if (!showBudgetBanner) return null;
        const message = poolExhausted
            ? "All of your providers' Pro-Listings are paused due to insufficient budget. Increase budgets to resume visibility on Klarity Health."
            : `${exhaustedProviderCount} of ${proListingCount} providers' Pro-Listings are paused due to insufficient budget. Increase their budgets to resume visibility on Klarity Health.`;
        return (
            <Alert
                className={s.budgetBanner}
                message={message}
                type="info"
                showIcon
                action={
                    <div className={s.budgetBannerAction} onClick={() => onSetupClick?.()}>
                        Resume now
                    </div>
                }
                icon={
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8.25706 3.09858C9.02167 1.73928 10.9788 1.73928 11.7434 3.09858L17.3237 13.0191C18.0736 14.3523 17.1102 15.9996 15.5805 15.9996H4.4199C2.89025 15.9996 1.92682 14.3523 2.67675 13.0191L8.25706 3.09858ZM11.0001 12.9998C11.0001 13.552 10.5524 13.9998 10.0001 13.9998C9.44784 13.9998 9.00012 13.552 9.00012 12.9998C9.00012 12.4475 9.44784 11.9998 10.0001 11.9998C10.5524 11.9998 11.0001 12.4475 11.0001 12.9998ZM10.0001 4.99976C9.44784 4.99976 9.00012 5.44747 9.00012 5.99976V8.99976C9.00012 9.55204 9.44784 9.99976 10.0001 9.99976C10.5524 9.99976 11.0001 9.55204 11.0001 8.99976V5.99976C11.0001 5.44747 10.5524 4.99976 10.0001 4.99976Z"
                            fill="#FBBF24"
                        />
                    </svg>
                }
            />
        );
    };

    const handleBannerCta = () => {
        if (!bannerConfig) return;
        if (bannerConfig.ctaAction === 'addProvider') {
            setAddProviderOpen(true);
        } else if (bannerConfig.ctaAction === 'setupProListing') {
            onSetupClick?.();
        }
    };

    const perfBannerRender = () => {
        if (!bannerConfig) return null;
        return (
            <div className={`${s.callout} ${s[bannerConfig.variant]}`}>
                <div className={s.calloutIcon}>
                    {bannerConfig.variant === 'amber' ? (
                        <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 1.667a8.333 8.333 0 100 16.666 8.333 8.333 0 000-16.666zm.833 12.5H9.167v-1.667h1.666v1.667zm0-3.334H9.167V5.833h1.666v5z" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 2l1.5 4.5L16 8l-4.5 1.5L10 14l-1.5-4.5L4 8l4.5-1.5L10 2z" />
                        </svg>
                    )}
                </div>
                <div className={s.calloutBody}>
                    <div className={s.calloutTitle}>{bannerConfig.title}</div>
                    <div className={s.calloutText}>{bannerConfig.text}</div>
                </div>
                <div className={s.calloutCta}>
                    <Button className={s.calloutBtn} onClick={handleBannerCta}>
                        {bannerConfig.ctaLabel}
                    </Button>
                </div>
            </div>
        );
    };

    const showUpsellCard = proListingCount === 0 && !overviewLoading && !poolExhausted;

    return (
        <div className={s.container}>
            {showBudgetBanner ? budgetBannerRender() : perfBannerRender()}

            {/* Listing performance section */}
            <section>
                <div className={s.header}>
                    <div>
                        <h2 className={s.title}>{hasProListing ? 'Listing performance' : 'Performance'}</h2>
                        <div className={s.subtitle}>Your total performance across Standard and Pro-Listings.</div>
                    </div>
                    <div className={s.controls}>
                        <Select
                            value={selectedProvider}
                            onChange={setSelectedProvider}
                            className={s.select}
                            style={{ minWidth: '160px' }}
                            dropdownStyle={{ minWidth: '200px' }}
                        >
                            {providerOptions.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            value={selectedSpecialty}
                            onChange={setSelectedSpecialty}
                            className={s.select}
                            style={{ minWidth: '160px' }}
                            dropdownStyle={{ minWidth: '220px' }}
                        >
                            {specialtyOptions.map((specialty) => (
                                <Option key={specialty} value={specialty}>
                                    {specialty}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            value={selectedPeriod}
                            onChange={setSelectedPeriod}
                            className={s.select}
                            dropdownStyle={{ minWidth: '160px' }}
                        >
                            {periodOptions.map((o) => (
                                <Option key={o.value} value={o.value} disabled={o.disabled}>
                                    {o.label}
                                </Option>
                            ))}
                        </Select>
                    </div>
                </div>

                <Spin spinning={metricsLoading || overviewLoading}>
                    <div
                        className={s.metricsGrid}
                        style={{ gridTemplateColumns: hasProListing ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)' }}
                    >
                        <div className={s.metricCard}>
                            <div className={s.metricHeader}>
                                <span className={s.metricLabel}>Bookings</span>
                                <Tooltip title={tooltips.bookings} color="#fff" overlayInnerStyle={{ color: '#333' }}>
                                    <QuestionCircleOutlined className={s.infoIcon} />
                                </Tooltip>
                            </div>
                            <div className={s.metricContent}>
                                <div className={s.metricValue}>
                                    <span className={s.value}>{metrics.totalBookings || 0}</span>
                                    {!isNil(metrics.previousTotalBookings) && (
                                        <span className={s.baseValue}>from {metrics.previousTotalBookings}</span>
                                    )}
                                </div>
                                {!isNil(metrics.totalBookingsChangeRate) && (
                                    <div
                                        className={`${s.changeValue} ${(metrics.totalBookingsChangeRate < 0 && s.danger) || (metrics.totalBookingsChangeRate > 0 && s.positive)}`}
                                    >
                                        {formatChange(metrics.totalBookingsChangeRate)}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={s.metricCard}>
                            <div className={s.metricHeader}>
                                <span className={s.metricLabel}>Impressions</span>
                                <Tooltip
                                    title={tooltips.impressions}
                                    color="#fff"
                                    overlayInnerStyle={{ color: '#333' }}
                                >
                                    <QuestionCircleOutlined className={s.infoIcon} />
                                </Tooltip>
                            </div>
                            <div className={s.metricContent}>
                                <div className={s.metricValue}>
                                    <span className={s.value}>{metrics.totalImpressions || 0}</span>
                                    {!isNil(metrics.previousTotalImpressions) && (
                                        <span className={s.baseValue}>from {metrics.previousTotalImpressions}</span>
                                    )}
                                </div>
                                {!isNil(metrics.impressionsChangeRate) && (
                                    <div
                                        className={`${s.changeValue} ${(metrics.impressionsChangeRate < 0 && s.danger) || (metrics.impressionsChangeRate > 0 && s.positive)}`}
                                    >
                                        {formatChange(metrics.impressionsChangeRate)}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={s.metricCard}>
                            <div className={s.metricHeader}>
                                <span className={s.metricLabel}>Impression share</span>
                                <Tooltip
                                    title={tooltips.impressionShare}
                                    color="#fff"
                                    overlayInnerStyle={{ color: '#333' }}
                                >
                                    <QuestionCircleOutlined className={s.infoIcon} />
                                </Tooltip>
                            </div>
                            <div className={s.metricContent}>
                                <div className={s.metricValue}>
                                    <span className={s.value}>{metrics.impressionShareRate}%</span>
                                    {!isNil(metrics.previousImpressionShareRate) && (
                                        <span className={s.baseValue}>from {metrics.previousImpressionShareRate}%</span>
                                    )}
                                </div>
                                {!isNil(metrics.impressionShareChangeRate) && (
                                    <div
                                        className={`${s.changeValue} ${(metrics.impressionShareChangeRate < 0 && s.danger) || (metrics.impressionShareChangeRate > 0 && s.positive)}`}
                                    >
                                        {formatChange(metrics.impressionShareChangeRate)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {hasProListing && (
                            <div className={s.metricCard}>
                                <div className={s.metricHeader}>
                                    <span className={s.metricLabel}>Total spend</span>
                                    <Tooltip
                                        title={tooltips.totalSpend}
                                        color="#fff"
                                        overlayInnerStyle={{ color: '#333' }}
                                    >
                                        <QuestionCircleOutlined className={s.infoIcon} />
                                    </Tooltip>
                                </div>
                                <div className={s.metricContent}>
                                    <div className={s.metricValue}>
                                        <span className={s.value}>${metrics.totalSpend}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Spin>
            </section>

            {/* Pro-Listing upsell card */}
            {showUpsellCard && (
                <div className={s.groupUpsellCard}>
                    <img src={RocketImg} alt="" className={s.groupUpsellRocket} />
                    <div className={s.groupUpsellHeader}>
                        <div className={s.groupUpsellEyebrow}>Reach more patients</div>
                        <h2 className={s.groupUpsellHeadline}>
                            <span className={s.brandText}>Get seen first</span> when patients search for care
                        </h2>
                        <p className={s.groupUpsellSub}>
                            You&apos;re just getting started — add more providers to your listing, then turn on
                            Pro-Listing to get top search placement and more exposure. You only pay when a patient
                            books.
                        </p>
                        <div className={s.groupUpsellFeatures}>
                            <div className={s.groupUpsellFeature}>
                                <div className={s.groupUpsellFeatIcon}>
                                    <svg
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        stroke="#0E62F4"
                                        strokeWidth="1.7"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="9" cy="9" r="6" />
                                        <line x1="13.25" y1="13.25" x2="17" y2="17" />
                                    </svg>
                                </div>
                                <span>Top search placement</span>
                            </div>
                            <div className={s.groupUpsellFeature}>
                                <div className={s.groupUpsellFeatIcon}>
                                    <svg
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        stroke="#0E62F4"
                                        strokeWidth="1.7"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect x="2.5" y="5" width="15" height="10" rx="1.5" />
                                        <line x1="2.5" y1="9" x2="17.5" y2="9" />
                                        <line x1="7" y1="5" x2="7" y2="15" />
                                    </svg>
                                </div>
                                <span>Dedicated practice page</span>
                            </div>
                            <div className={s.groupUpsellFeature}>
                                <div className={s.groupUpsellFeatIcon}>
                                    <svg
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        stroke="#0E62F4"
                                        strokeWidth="1.7"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect x="2.5" y="4" width="15" height="11" rx="1.5" />
                                        <circle cx="7" cy="9" r="1.2" />
                                        <path d="M2.5 13l4-4 4.5 4.5" />
                                        <path d="M11 11.5l3-3 3.5 3.5" />
                                    </svg>
                                </div>
                                <span>Featured on provider listing</span>
                            </div>
                        </div>
                        <div className={s.groupUpsellActions}>
                            <Button type="primary" className={s.groupUpsellBtnPrimary} onClick={onSetupClick}>
                                Set up Pro-listing
                            </Button>
                            <Button className={s.groupUpsellBtnSecondary} onClick={() => setShowWebinarBooking(true)}>
                                Talk to specialist
                            </Button>
                        </div>
                    </div>
                    <div className={s.groupUpsellStat}>
                        <div className={s.groupUpsellStatLabel}>Providers on Pro-Listing</div>
                        <div className={s.groupUpsellStatValue}>{proListingCount} / 5</div>
                        <div className={s.groupUpsellStatSub}>
                            {stdListingCount < 5
                                ? `Add ${5 - stdListingCount} more provider${5 - stdListingCount === 1 ? '' : 's'} to unlock perks`
                                : 'Enable Pro-Listing on 5 to unlock perks'}
                        </div>
                        <div className={s.groupUpsellInfo}>
                            <svg className={s.groupUpsellInfoIcon} viewBox="0 0 20 20" fill="#0E62F4">
                                <path d="M10 1.5l1.5 4.5L16 7.5l-4.5 1.5L10 13.5l-1.5-4.5L4 7.5l4.5-1.5L10 1.5z" />
                                <path d="M16.5 13.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" opacity="0.7" />
                            </svg>
                            <div className={s.groupUpsellInfoContent}>
                                <p className={s.groupUpsellInfoText}>
                                    Enable Pro-Listing for 5+ providers to <strong>unlock premium features</strong> — a
                                    dedicated <strong>group practice page</strong> that drives bookings across your
                                    practice, and an <strong>enhanced carousel</strong> on listing pages that showcases
                                    your team to every visitor, accelerating patient acquisition.
                                </p>
                                <a
                                    href="#"
                                    className={s.groupUpsellInfoLink}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onSetupClick?.();
                                    }}
                                >
                                    Learn more →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Performance trend */}
            <Spin spinning={trendLoading}>
                <div className={s.chartSection}>
                    <div className={s.chartHeader}>
                        <h3 className={s.chartTitle}>Performance trend</h3>
                        <div className={s.trendControls}>
                            <Select
                                value={trendProvider}
                                onChange={setTrendProvider}
                                className={s.select}
                                style={{ minWidth: '160px' }}
                                dropdownStyle={{ minWidth: '200px' }}
                            >
                                {providerOptions.map((opt) => (
                                    <Option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                value={trendSpecialty}
                                onChange={setTrendSpecialty}
                                className={s.select}
                                style={{ minWidth: '160px' }}
                                dropdownStyle={{ minWidth: '220px' }}
                            >
                                {specialtyOptions.map((specialty) => (
                                    <Option key={specialty} value={specialty}>
                                        {specialty}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                value={trendPeriod}
                                onChange={setTrendPeriod}
                                className={s.select}
                                dropdownStyle={{ minWidth: '160px' }}
                            >
                                {periodOptions.map((o) => (
                                    <Option key={o.value} value={o.value} disabled={o.disabled}>
                                        {o.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                    <div className={s.trendTabs}>
                        <span
                            className={`${s.chip} ${trendTab === 'overview' ? s.chipActive : ''}`}
                            onClick={() => setTrendTab('overview')}
                        >
                            Overview
                        </span>
                        <span
                            className={`${s.chip} ${trendTab === 'byProvider' ? s.chipActive : ''}`}
                            onClick={() => setTrendTab('byProvider')}
                        >
                            By provider
                        </span>
                    </div>
                    {trendTab === 'overview' ? (
                        <PerformanceChart
                            data={trendDisplay}
                            specialty={trendSpecialty === 'All specialties' ? undefined : trendSpecialty}
                        />
                    ) : (
                        <ByProviderTable
                            providers={byProviderRows}
                            hasProListing={hasProListing}
                            stdListingCount={stdListingCount}
                            onSetupClick={onSetupClick}
                        />
                    )}
                </div>
            </Spin>

            <WebinarBooking
                modalHeader={
                    <div className={s.webinarHeader}>
                        <div className={s.webinarTitle}>Talk to a Pro-Listings specialist</div>
                        <div className={s.webinarDesc}>
                            Want to grow faster with pro listings? Book a time to speak with our specialist and get
                            personalized guidance.
                        </div>
                    </div>
                }
                show={showWebinarBooking}
                link={CALENDLY_LINK.PRO_LISTINGS_SPECIALIST}
                webinarScheduleType={EWebinarScheduleType.PRO_LISTINGS_SPECIALIST}
                onClose={() => setShowWebinarBooking(false)}
            />
            <AddProviderModal open={addProviderOpen} onClose={() => setAddProviderOpen(false)} />
        </div>
    );
};

export default GroupPerformance;
